/**
 * Calculates how many install days a project requires and generates
 * available dates starting from a given date.
 */

/**
 * Calculate the number of install days needed.
 * Uses custom rounding: <= .4 rounds down, >= .5 rounds up.
 */
export function calculateInstallDays(projectTotal: number, ivpd: number): number {
  if (ivpd <= 0) return 1;
  const raw = projectTotal / ivpd;
  const floor = Math.floor(raw);
  const fraction = raw - floor;
  const days = fraction >= 0.5 ? floor + 1 : floor;
  return Math.max(days, 1);
}

/**
 * Parse a currency string like "$5,000" or "$12,345" into a number.
 * Returns 0 if the string cannot be parsed.
 */
export function parseCurrencyString(value: string): number {
  if (!value || value === '—') return 0;
  const cleaned = value.replace(/[^0-9.]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Select `count` dates from `availableDates` starting from `startDate`.
 * Only dates on or after `startDate` are considered.
 * The available dates come from the API (actual foreman availability, not a weekly pattern).
 *
 * @param startDate - The clicked date (first candidate)
 * @param count - Number of dates to collect
 * @param availableDates - Actual available dates from the API (may be UTC-midnight)
 * @returns Array of local-midnight Date objects for the scheduled install dates
 */
export function generateInstallDates(
  startDate: Date,
  count: number,
  availableDates: Date[],
): Date[] {
  const startLocal = toLocalMidnight(startDate);
  const startTime = startLocal.getTime();

  const candidates = availableDates
    .map(d => toLocalMidnight(d))
    .filter(d => d.getTime() >= startTime)
    .sort((a, b) => a.getTime() - b.getTime());

  return candidates.slice(0, count);
}

/**
 * Return `count` consecutive calendar days starting at `startDate`, regardless of availability.
 * Used for crew members (e.g. co-foremen, field superintendents) whose schedules are not
 * constrained by per-foreman availability records.
 */
export function generateConsecutiveDates(startDate: Date, count: number): Date[] {
  const start = toLocalMidnight(startDate);
  const dates: Date[] = [];
  for (let i = 0; i < count; i++) {
    dates.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return dates;
}

/**
 * Normalize a UTC-midnight Date to local midnight to avoid timezone shift.
 */
function toLocalMidnight(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Format a Date to 'YYYY-MM-DD' string (local timezone).
 */
export function toDateString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
